import { connect } from 'react-redux';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import renderIf from 'render-if';
import $ from 'jquery';
import { isEqual, debounce } from 'lodash';
import BarRow from '../rows/bar_row';
import Ticks from '../ticks';
import { hash } from 'components/timeline_control/helpers';
import classNames from 'classnames';
import DependencyLines from '../dependency_lines/dependency_lines';

const VISIBLE_RECORD_INCREMENT = 200;

const debouncedPublishingReady = debounce(() => {
  window.publishingStatus.isReady = true;
}, 2000);

function getAbsolutePositionRelativeToContainer(element, container) {
  var elementRect = element.getBoundingClientRect();
  var containerRect = container.getBoundingClientRect();

  var absolutePosition = {
    top: elementRect.top - containerRect.top,
    height: elementRect.height,
    left: elementRect.left - containerRect.left,
    right: elementRect.right - containerRect.left,
  };

  return absolutePosition;
}

/**
 * Give refs to the bars, calculate their positions relative to the container
 * @param {Map<string, HTMLElement>} barReferences
 */
function calculateBarPositions(barReferences) {
  const scrollWrapper = document.querySelector('.timeline-content');

  const barPositions = {};

  for (const [key, barDOMElement] of barReferences.entries()) {
    if (barDOMElement) {
      barPositions[key] = getAbsolutePositionRelativeToContainer(
        barDOMElement,
        scrollWrapper
      );
    }
  }

  return barPositions;
}

function collectByProgenitor(items, acc) {
  return items.reduce((acc, item) => {
    if (!acc.has(item.progenitorIndex)) acc.set(item.progenitorIndex, []);
    acc.get(item.progenitorIndex).push(item);
    if (item.children) {
      return collectByProgenitor(item.children, acc);
    }
    return acc;
  }, acc || new Map());
}

/**
 * @typedef TimelineRowsProps
 * @prop {*=} resourceTree
 * @prop {*=} scale
 * @prop {*=} scrollerRef
 * @prop {*=} timelineConfig
 * @prop {*=} zoom
 * @prop {*=} zoomRef
 * @prop {*=} dependencyLinks
 * @prop {*=} showMilestoneBars
 * @prop {*=} extraHeight
 */
/**
 * @type {React.FC<TimelineRowsProps>}
 */
function TimelineRows({
  resourceTree,
  scale,
  scrollerRef,
  timelineConfig,
  zoom,
  zoomRef,
  dependencyLinks,
  showMilestoneBars,
  extraHeight,
}) {
  const { milestones } = timelineConfig;
  const [visibleRows, setVisibleRows] = useState(25);
  const [calculatedBarPositions, setCalculatedBarPositions] = useState(null);
  const rowsByProgenitor = useMemo(
    () => collectByProgenitor(resourceTree.tree),
    [resourceTree.tree]
  );

  /**
   * @type {React.MutableRefObject<Map<string, HTMLElement> | null>} each value will be an element,
   * set by a callback ref
   */
  const barReferencesRef = useRef(null);
  if (barReferencesRef.current == null) {
    // normally you shouldn't write a ref during render - ok here because this is idempotent initialization

    barReferencesRef.current = new Map();
  }

  useEffect(() => {
    const isMilestone = timelineConfig.timelineType === 'milestone';

    const scroller = scrollerRef.current;

    const updateBarLabels = () => {
      window.requestAnimationFrame(() => {
        const { thickBars, showFloatingLabels } = timelineConfig;

        const scrollLeft = scrollerRef?.current?.scrollLeft || 0;

        $(scroller)
          .find('[data-bar-left]')
          .each(function () {
            const barLeft = parseInt(this.dataset.barLeft, 10);
            const width = parseInt(this.dataset.barWidth, 10);
            const labelWidth = parseInt(this.dataset.labelWidth, 10);

            // if the bar is only partially visible on the left side, use a fixed label
            const fixedLabel =
              barLeft <= scrollLeft && scrollLeft <= barLeft + width;

            // check to see if the label is wider than the bar itself
            const labelIsTooBig =
              scrollLeft <= barLeft + width && labelWidth >= width;

            // For thick bars only: if the label can't fit within the visible
            // portion of the bar, or the label is too big, then place the
            // label outside the bar
            let bumpLabel = false;
            if (thickBars) {
              bumpLabel =
                (barLeft + width - labelWidth <= scrollLeft &&
                  scrollLeft < barLeft + width) ||
                labelIsTooBig;
            }

            // once the bar goes off the screen, hide the label completely
            const hideLabel = barLeft + width < scrollLeft;

            const infinite_bar =
              $(this).children('.unboundBoth').length !== 0 ||
              $(this).children('.hasToDateOnly').length !== 0;
            const infinite_bar_both =
              $(this).children('.unboundBoth').length !== 0;

            $(this).toggleClass(
              'fixed-label',
              (fixedLabel && !bumpLabel && showFloatingLabels) || infinite_bar
            );
            $(this).toggleClass(
              'bump-label',
              (bumpLabel && !infinite_bar_both) || isMilestone
            );
            $(this).toggleClass('hide-label', hideLabel && !infinite_bar_both);
          });
      });
    };

    // use a timeout for initial update to make sure it happens after the measurements are up to date
    setTimeout(updateBarLabels, 100);

    scroller.addEventListener('scroll', updateBarLabels);
    return () => scroller.removeEventListener('scroll', updateBarLabels);
  }, [timelineConfig, zoom, scale, visibleRows, resourceTree, scrollerRef]);

  useEffect(() => {
    if (!window.isPublishing && resourceTree.rows.length > visibleRows) {
      const id = window.setTimeout(
        () => setVisibleRows(visibleRows + VISIBLE_RECORD_INCREMENT),
        1000
      );
      return () => window.clearTimeout(id);
    }
  }, [resourceTree.rows.length, visibleRows]);

  // Use a layout effect to ensure bar references are up to date anytime this component is
  // rerendered, without a visible flicker
  useLayoutEffect(() => {
    const barReferences = barReferencesRef.current;

    if (timelineConfig?.showDependencies && barReferences) {
      const newPositions = calculateBarPositions(barReferences);
      setCalculatedBarPositions(currentPositions => {
        if (!isEqual(currentPositions, newPositions)) {
          return newPositions;
        }
        return currentPositions;
      });
    }
  }, [timelineConfig, zoom, scale, visibleRows]);

  const setBarReference = useCallback((pathId, el) => {
    const barReferences = barReferencesRef.current;
    if (barReferences) {
      if (el) {
        barReferences.set(pathId, el);
      } else {
        barReferences.delete(pathId);
      }
    }
  }, []);

  const style = {
    width: 'var(--content-width)',
    overflowX: zoom === 'fit' ? 'hidden' : 'auto',
  };

  if (window.isPublishing) {
    debouncedPublishingReady();
  }

  return (
    <div className='timeline-content-scroll-wrapper' ref={zoomRef}>
      <div
        data-record-preview-bounds-container
        ref={scrollerRef}
        className='timeline-content-outer timeline-control'
        style={style}
      >
        <div className='timeline-content'>
          <Ticks
            scale={scale}
            width={scale.totalPixelWidth()}
            milestones={timelineConfig.milestonesShowLines ? milestones : []}
          />
          {renderIf(timelineConfig?.showDependencies)(() => {
            return (
              <DependencyLines
                barPositions={calculatedBarPositions}
                dependencyLinks={dependencyLinks}
              />
            );
          })}
          {renderIf(
            timelineConfig?.milestones.length > 0 &&
              showMilestoneBars &&
              !timelineConfig?.viewOnly
          )(() => {
            return (
              <div
                key='milestone-spacer'
                className='timeline-milestone-row'
                style={{
                  width: scale.totalPixelWidth(),
                  height: `calc(var(--custom-timeline-milestone-swimlane-height) + ${extraHeight}px)`,
                }}
              />
            );
          })}
          <Rows
            {...{
              resourceTree,
              timelineConfig,
              visibleRows,
              rowsByProgenitor,
              scale,
              setBarReference,
            }}
          />
        </div>
      </div>
    </div>
  );
}

const Rows = React.memo(function Rows({
  resourceTree,
  timelineConfig,
  visibleRows,
  rowsByProgenitor,
  scale,
  setBarReference,
}) {
  function buildRow(resource, pathParents, parentHeight = 0) {
    if (resource.isLastColumn) {
      return buildLastColumn(resource, pathParents, parentHeight);
    } else {
      return buildChildren(resource, pathParents, parentHeight);
    }
  }

  function buildChildren(resource, pathParents, parentHeight) {
    const height = Math.max(
      resource.height / resource.children.length,
      parentHeight
    );

    return resource.children.map((subResource, childIndex) => {
      return buildRow(subResource, [...pathParents, childIndex], height);
    });
  }

  function buildLastColumn(resource, pathParents, parentHeight) {
    let rows;
    if (timelineConfig.compactView) {
      rows = buildCompactRows(resource, pathParents);
    } else {
      rows = buildExpandedRows(resource, pathParents);
    }

    if (!window.isPublishing) {
      const availableRows = visibleRows || 0;
      rows = rows.slice(0, Math.min(availableRows, rows.length));
    }

    const key = `TimelineRows-${resource.unique_key}-${rows.length}`;

    const progenitor = rowsByProgenitor.get(resource.progenitorIndex);
    const isLastRow =
      progenitor[progenitor.length - 1].unique_key === resource.unique_key;

    return (
      <div
        key={key}
        className={classNames({
          'timeline-row': true,
          'timeline-row--content': true,
          'timeline-row--boundary-row': isLastRow,
        })}
        style={rowStyle(resource, parentHeight)}
      >
        {rows}
      </div>
    );
  }

  function rowStyle(resource, parentHeight) {
    return {
      minHeight: Math.max(resourceHeight(resource), parentHeight),
      width: scale.totalPixelWidth(),
    };
  }

  function resourceHeight(resource) {
    return resource.heightOfRows(
      timelineConfig.compactView,
      0,
      scale.minDate,
      scale.maxDate,
      timelineConfig.thickBars,
      timelineConfig.fontSize
    );
  }

  function buildCompactRows(resource, pathParents) {
    return resource.groupedRows.map((group, index) => {
      const lastRow = index === resource.groupedRows.length - 1;
      const hashed = hash(`${group[0].label}`);
      const key = `TimelineRows-${hashed}`;

      return (
        <BarRow
          key={key}
          scale={scale}
          bars={group}
          lastRow={lastRow}
          barHash={hashed}
          timelineConfig={timelineConfig}
          pathParents={pathParents}
          setBarReference={setBarReference}
        />
      );
    });
  }

  function buildExpandedRows(resource, pathParents) {
    const rows = resource.getRows(scale.minDate, scale.maxDate);

    return rows.map((row, index) => {
      const lastRow = index === rows.length - 1;
      const hashed = hash(`${row.label}${row.from}${row.to}${index}`);
      const key = `TimelineRows-${hashed}`;

      return (
        <BarRow
          key={key}
          scale={scale}
          bars={[row]}
          lastRow={lastRow}
          barHash={hashed}
          timelineConfig={timelineConfig}
          pathParents={pathParents}
          setBarReference={setBarReference}
        />
      );
    });
  }

  return resourceTree.tree.map((resource, treeRowIndex) => {
    // See the construction of dependencyLinks in timeline_data.js
    // for a description of how we build the path indexes.
    // Basically we use the resources position in each column.
    return buildRow(resource, [treeRowIndex]);
  });
});

const mapStateToProps = state => {
  const timelineConfig = state.getIn(['timelineConfig']);
  const zoom = state.getIn(['dateRange', 'zoom']);

  return {
    timelineConfig,
    zoom,
  };
};

export default connect(mapStateToProps)(TimelineRows);
